import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { useCallback } from 'react';
import { alertsApi, ApiError, type TraderMessage } from '~/lib/api';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { InlineError } from '~/components/ErrorPage';
import { useMessageStream } from '~/hooks/useMessageStream';

function parseUtc(ts: string): Date {
  if (!ts) return new Date(NaN);
  if (ts.includes('T')) return new Date(ts);
  return new Date(ts.replace(' ', 'T') + 'Z');
}

function formatTime(ts: string): string {
  return parseUtc(ts).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelative(ts: string): string {
  return formatDistanceToNow(parseUtc(ts), { addSuffix: true });
}

function extractImageUrls(rawPayload: string | null | undefined): string[] {
  if (!rawPayload) return [];
  try {
    const payload = JSON.parse(rawPayload) as {
      attachments?: Array<{ url?: string; type?: string; mimeType?: string | null }>;
    };
    const urls: string[] = [];
    for (const a of payload.attachments ?? []) {
      if (!a.url) continue;
      const mime = a.mimeType || a.type || '';
      if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp)(\?|$)/i.test(a.url)) {
        urls.push(a.url);
      }
    }
    return urls;
  } catch {
    return [];
  }
}

function AlertsContent() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['alerts'],
    queryFn: () => alertsApi.list({ limit: 100 }),
    // Poll-based freshness as a simple fallback; WS drives the real-time path.
    refetchInterval: 60_000,
  });

  // Live refresh on any new message — server filters to followed traders,
  // but since the broadcast is unfiltered we just invalidate and refetch
  // rather than try to filter client-side against a separate follow list.
  // Refetch is cheap (last 100 messages).
  const handleNewMessage = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['alerts'] });
  }, [queryClient]);

  useMessageStream({ enabled: true, onMessage: handleNewMessage });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return <InlineError error={error instanceof ApiError ? error : (error as Error)} />;
  }

  const messages = data?.messages ?? [];

  // Group by day for readability
  const byDay = new Map<string, TraderMessage[]>();
  for (const m of messages) {
    const day = parseUtc(m.timestamp).toISOString().slice(0, 10);
    let arr = byDay.get(day);
    if (!arr) {
      arr = [];
      byDay.set(day, arr);
    }
    arr.push(m);
  }
  const dayKeys = Array.from(byDay.keys());

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Alerts</h1>
        <p className="text-sm text-text-secondary">
          Recent messages from traders you follow.{' '}
          <Link to="/traders" className="text-accent hover:underline">
            Browse all traders
          </Link>{' '}
          to follow more.
        </p>
      </div>

      {messages.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-text-secondary">
            No alerts yet. Open a trader's page and click{' '}
            <span className="text-accent font-mono">Follow</span> to start receiving their
            messages here.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {dayKeys.map((day) => (
            <div key={day} className="space-y-3">
              <h2 className="text-xs font-mono uppercase tracking-widest text-text-muted">
                {day}
              </h2>
              <div className="space-y-2">
                {byDay.get(day)!.map((m) => (
                  <AlertCard key={m.id} message={m} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AlertCard({ message }: { message: TraderMessage }) {
  const imageUrls = extractImageUrls(message.raw_payload);
  const isAlertChannel = message.channel_type === 'alerts';
  const hasReply = !!message.reply_to_author;

  return (
    <div
      className={clsx(
        'card p-3 sm:p-4 space-y-2',
        isAlertChannel && 'border-warning/30'
      )}
    >
      {hasReply && (
        <div className="text-xs text-text-muted border-l-2 border-border pl-2 truncate">
          <span className="text-text-secondary">↩ Replying to @{message.reply_to_author}</span>
          {message.reply_to_content && (
            <span className="ml-1 text-text-muted">· "{message.reply_to_content}"</span>
          )}
        </div>
      )}

      <div className="flex items-baseline gap-2 flex-wrap text-xs text-text-muted">
        <Link
          to={`/traders/${message.trader_id}`}
          className="text-text-primary font-medium hover:text-accent"
        >
          @{message.author_username}
        </Link>
        <span>·</span>
        <span className="font-mono">{formatTime(message.timestamp)}</span>
        <span>·</span>
        <span title={formatRelative(message.timestamp)}>{formatRelative(message.timestamp)}</span>
        {message.channel_name && (
          <span className="text-text-muted">· #{message.channel_name}</span>
        )}
        {isAlertChannel && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-warning/15 text-warning">
            alert
          </span>
        )}
      </div>

      {message.content && (
        <p className="text-sm text-text-primary whitespace-pre-wrap break-words">
          {message.content}
        </p>
      )}

      {imageUrls.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {imageUrls.map((url, i) => (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block max-w-sm overflow-hidden rounded border border-border hover:border-accent transition-colors"
            >
              <img
                src={url}
                alt={`attachment ${i + 1}`}
                loading="lazy"
                className="max-h-80 w-auto object-contain"
              />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AlertsPage() {
  return (
    <ErrorBoundary>
      <AlertsContent />
    </ErrorBoundary>
  );
}
